import React from 'react';
import { FiMail, FiLock, FiEye, FiEyeOff, FiAlertCircle } from 'react-icons/fi';
import SimplePasswordChangeModal from './SimplePasswordChangeModal';
import ForgotUsernameModal from './ForgotUsernameModal';
import ForgotPasswordModal from './ForgotPasswordModal';
import useSimpleLogin from './useSimpleLogin';
import {
  LoginContainer, Form, FormGroup, InputWrapper, Input, Icon, PasswordToggle, Button,
  EnrollNowButton, AlertBanner, AlertIcon, AlertText, GreetingWrapper, ForgotUsernameLink,
  HelpLink, LoadingSpinner
} from './SimpleLogin.styles';
// Note: SimpleLogin is rendered inside AuthContainer/AuthCard from App.js
// So we don't need AuthPage wrapper here, just render content directly

function SimpleLogin() {
  const {
    formData, showPassword, setShowPassword, loading, error,
    showPasswordChangeModal, setShowPasswordChangeModal,
    showForgotUsernameModal, setShowForgotUsernameModal,
    showForgotPasswordModal, setShowForgotPasswordModal,
    showSessionExpired, showLoggedOut, location, navigate,
    handleChange, handleSubmit, handlePasswordChangeSuccess, handleNeedHelp
  } = useSimpleLogin();

  return (
    <>
      <LoginContainer>
        <GreetingWrapper>
          <span className="arabic-text">السلام عليكم</span>
        </GreetingWrapper>

        <Form onSubmit={handleSubmit}>
            {/* All error messages displayed above email input */}
            {showLoggedOut && (
              <AlertBanner
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                role="alert"
              >
                <AlertIcon>
                  <FiAlertCircle />
                </AlertIcon>
                <AlertText>
                  You have been logged out. Please sign in again to continue.
                </AlertText>
              </AlertBanner>
            )}

            {showSessionExpired && (
              <AlertBanner
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                role="alert"
              >
                <AlertIcon>
                  <FiAlertCircle />
                </AlertIcon>
                <AlertText>
                  Your session has expired. Please sign in again to continue.
                </AlertText>
              </AlertBanner>
            )}

            {error && (
              <AlertBanner
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                role="alert"
              >
                <AlertIcon>
                  <FiAlertCircle />
                </AlertIcon>
                <AlertText>
                  {error}
                </AlertText>
              </AlertBanner>
            )}

            <FormGroup>
              <InputWrapper>
                <Icon>
                  <FiMail size={16} />
                </Icon>
                <Input
                  type="email"
                  name="email"
                  placeholder="Email address"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Icon>
                  <FiLock size={16} />
                </Icon>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </PasswordToggle>
              </InputWrapper>
            </FormGroup>

            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <LoadingSpinner />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
        </Form>

        <EnrollNowButton className="enroll-now-button" onClick={() => {
            // Preserve redirect parameter when navigating to signup
            const urlParams = new URLSearchParams(location.search);
            const redirectParam = urlParams.get('redirect');
            const redirectState = location.state?.from?.pathname;

            navigate('/signup', {
              state: {
                from: redirectParam || redirectState ? { pathname: redirectParam || redirectState } : { pathname: '/catalog' }
              },
              search: redirectParam ? `?redirect=${encodeURIComponent(redirectParam)}` : ''
            });
          }}>
          Enroll Now
        </EnrollNowButton>

        <ForgotUsernameLink>
          <button onClick={() => setShowForgotUsernameModal(true)}>
            Forgot your email?
          </button>
          <span className="separator">|</span>
          <button onClick={() => setShowForgotPasswordModal(true)}>
            Forgot your password?
          </button>
        </ForgotUsernameLink>

        <HelpLink>
          <button onClick={handleNeedHelp}>
            Need help?
          </button>
        </HelpLink>
      </LoginContainer>

      <SimplePasswordChangeModal
        isOpen={showPasswordChangeModal}
        onClose={() => setShowPasswordChangeModal(false)}
        onSuccess={handlePasswordChangeSuccess}
      />

      <ForgotUsernameModal
        isOpen={showForgotUsernameModal}
        onClose={() => setShowForgotUsernameModal(false)}
      />

      <ForgotPasswordModal
        isOpen={showForgotPasswordModal}
        onClose={() => setShowForgotPasswordModal(false)}
      />
    </>
  );
}

export default SimpleLogin;
